<template>
    <section class='main'>
        <div class='crumbs'>
            <el-breadcrumb separator='/'>
                <el-breadcrumb-item><i class='el-icon-lx-profile'></i> 数据存储</el-breadcrumb-item>
                <el-breadcrumb-item>云函数</el-breadcrumb-item>
                <el-breadcrumb-item v-if='selectedProjectAppId'>
                    {{ projectCnNameMap.get(selectedProjectAppId) }}
                </el-breadcrumb-item>
            </el-breadcrumb>
        </div>
        
        <div v-if='!selectedProjectAppId' class='container'>
            <div style='padding: 20px;'>
                <div style='margin-bottom: 12px; font-size: 14px; color: #606266; font-weight: 600'>选择项目</div>
                <el-select
                    v-model='selectedProjectAppId'
                    filterable
                    size='small'
                    placeholder='请选择项目以继续管理云函数'
                    style='width: 300px'
                    @change='handleProjectSelect'
                >
                    <el-option
                        v-for='item in projectInfoList'
                        :key='item.id'
                        :label='item.cnName'
                        :value='item.appId'
                    />
                </el-select>
            </div>
        </div>
        
        <div v-else class='container' style='min-width: 1080px'>
            <div class='plugins-tips'>
                <el-row>
                    <el-col :span='20'>
                        <el-button
                            type='primary'
                            size='mini'
                            icon='el-icon-back'
                            plain
                            style='margin-right: 15px'
                            @click='selectedProjectAppId = undefined'
                        >
                            切换项目
                        </el-button>
                        <el-form v-show='showSearch' :inline='true' :model='queryParams' size='small'
                                 style='display: inline-block'>
                            <el-form-item style='margin-bottom: 0'>
                                <el-select
                                    v-model='queryParams.type'
                                    clearable
                                    size='mini'
                                    placeholder='函数类型'
                                >
                                    <el-option
                                        v-for='item in typeOptions'
                                        :key='item.value'
                                        :label='item.label'
                                        :value='item.value'
                                    />
                                </el-select>
                            </el-form-item>
                            <el-form-item style='margin-bottom: 0'>
                                <el-input
                                    v-model.trim='queryParams.name'
                                    clearable
                                    size='mini'
                                    placeholder='函数名'
                                    style='width: 170px'
                                />
                            </el-form-item>
                            <el-form-item style='margin-bottom: 0'>
                                <el-button icon='el-icon-search' size='mini' type='primary' @click='handleQuery'>
                                    搜索
                                </el-button>
                                <el-button icon='el-icon-refresh' size='mini' @click='resetQuery'>重置</el-button>
                            </el-form-item>
                        </el-form>
                    </el-col>
                    <el-col :span='4' style='text-align: right'>
                        <el-button icon='el-icon-plus' plain size='mini' type='primary' @click='openAddDialog'>
                            新增云函数
                        </el-button>
                    </el-col>
                </el-row>
            </div>
            
            <el-table
                v-loading='loading'
                :cell-style="{ textAlign: 'center' }"
                :data='tableList'
                @sort-change='handleSortChange'
            >
                <el-table-column prop='name' align='center' label='函数名' min-width='180' show-overflow-tooltip />
                <el-table-column align='center' label='类型' width='120'>
                    <template slot-scope='scope'>
                        <el-tag size='mini' :type="scope.row.type === FUNCTION_TYPE.PROJECT ? 'primary' : 'success'">
                            {{ typeLabel(scope.row.type) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop='description' align='center' label='描述' min-width='220' show-overflow-tooltip />
                <el-table-column prop='version' align='center' label='最新版本' width='90' />
                <el-table-column align='center' label='测试环境' width='90'>
                    <template slot-scope='scope'>
                        <el-tag v-if='scope.row.testActive' size='mini' type='success'>激活</el-tag>
                        <span v-else>未激活</span>
                    </template>
                </el-table-column>
                <el-table-column align='center' label='生产环境' width='90'>
                    <template slot-scope='scope'>
                        <el-tag v-if='scope.row.proActive' size='mini' type='success'>激活</el-tag>
                        <span v-else>未激活</span>
                    </template>
                </el-table-column>
                <el-table-column prop='createTime' align='center' sortable='custom' label='创建时间' width='170' />
                <el-table-column prop='updateTime' align='center' sortable='custom' label='更新时间' width='170' />
                <el-table-column fixed='right' align='center' width='220' label='操作'>
                    <template slot-scope='scope'>
                        <el-button icon='el-icon-refresh' size='mini' type='text' @click='handleRefresh(scope.row)'>
                            刷新缓存
                        </el-button>
                        <el-button icon='el-icon-edit' size='mini' type='text' @click='openVersionDialog(scope.row)'>
                            修改
                        </el-button>
                        <el-button
                            v-if='scope.row.type === FUNCTION_TYPE.PROJECT'
                            icon='el-icon-delete'
                            size='mini'
                            type='text'
                            @click='handleDelete(scope.row)'
                        >
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
            
            <div class='pagination'>
                <el-pagination
                    :current-page='queryParams.pageNum'
                    :page-sizes='[15, 30, 45, 60, 75, 100]'
                    :page-size='queryParams.pageSize'
                    :total='queryParams.total'
                    background
                    layout='sizes, prev, pager, next'
                    @size-change='handleSizeChange'
                    @current-change='handlePagination'
                />
            </div>
        </div>
        
        <el-dialog :visible.sync='addDialogVisible' append-to-body width='520px' @closed='resetAddForm'>
            <span slot='title'>
                新增云函数 - <small style='color: #909399'>{{ projectCnNameMap.get(selectedProjectAppId) }}</small>
            </span>
            <el-alert
                title='新增仅填写元信息，脚本请在版本列表里编辑。'
                type='info'
                :closable='false'
                show-icon
                style='margin-bottom: 12px'
            />
            <el-form :model='addForm' label-width='100px' size='small'>
                <el-form-item label='函数类型' required>
                    <el-select v-model='addForm.type' style='width: 100%' @change='onAddTypeChange'>
                        <el-option
                            v-for='item in typeOptions'
                            :key='item.value'
                            :label='item.label'
                            :value='item.value'
                        />
                    </el-select>
                </el-form-item>
                <el-form-item v-if='addForm.type === FUNCTION_TYPE.PROJECT' label='函数名' required>
                    <el-input
                        v-model.trim='addForm.name'
                        maxlength='64'
                        show-word-limit
                        placeholder='字母开头，仅字母数字下划线'
                    />
                </el-form-item>
                <el-form-item v-else label='函数名'>
                    <el-input value='后端固定值' disabled />
                </el-form-item>
                <el-form-item label='描述'>
                    <el-input v-model.trim='addForm.description' type='textarea'
                              :autosize='{ minRows: 3, maxRows: 8 }' />
                </el-form-item>
            </el-form>
            <div slot='footer' class='dialog-footer'>
                <el-button @click='addDialogVisible = false'>取消</el-button>
                <el-button type='primary' :loading='addSubmitting' @click='submitAdd'>确定</el-button>
            </div>
        </el-dialog>
        
        <el-dialog
            :visible.sync='versionDialogVisible'
            append-to-body
            width='1200px'
            @closed='closeVersionDialog'
        >
            <span slot='title' class='version-dialog-title-row'>
                <span>{{ versionDialogTitle }}</span>
                <el-button
                    size='mini'
                    type='primary'
                    :disabled='versionLoading || !versionGroupRow'
                    @click='openImportScriptDialog'
                >
                    导入脚本
                </el-button>
            </span>
            <el-alert
                title='changeLog 只影响当前版本；description 会同步该函数全部版本。编辑脚本会创建新版本。'
                type='info'
                :closable='false'
                show-icon
                style='margin-bottom: 12px'
            />
            <el-table
                v-loading='versionLoading'
                :cell-style="{ textAlign: 'center' }"
                :data='versionList'
                size='small'
            >
                <el-table-column prop='version' align='center' label='版本号' width='90' />
                <el-table-column prop='changeLog' align='center' label='变更日志' min-width='220'
                                 show-overflow-tooltip />
                <el-table-column align='center' label='测试环境' width='90'>
                    <template slot-scope='scope'>
                        <el-tag v-if='scope.row.testActive' size='mini' type='success'>激活</el-tag>
                        <span v-else>未激活</span>
                    </template>
                </el-table-column>
                <el-table-column align='center' label='生产环境' width='90'>
                    <template slot-scope='scope'>
                        <el-tag v-if='scope.row.proActive' size='mini' type='success'>激活</el-tag>
                        <span v-else>未激活</span>
                    </template>
                </el-table-column>
                <el-table-column prop='createTime' align='center' label='创建时间' width='170' />
                <el-table-column prop='updateTime' align='center' label='更新时间' width='170' />
                <el-table-column fixed='right' align='center' width='380' label='操作'>
                    <template slot-scope='scope'>
                        <el-button type='text' size='mini' @click='openEditMeta(scope.row)'>修改信息</el-button>
                        <el-button type='text' size='mini' @click='deployVersion(scope.row, true)'>部署测试</el-button>
                        <el-button type='text' size='mini' @click='deployVersion(scope.row, false)'>部署生产</el-button>
                        <el-button
                            v-if='versionGroupRow && versionGroupRow.type === FUNCTION_TYPE.PROJECT'
                            type='text'
                            size='mini'
                            @click='openExecuteDialog(scope.row)'
                        >
                            执行函数
                        </el-button>
                        <el-button type='text' size='mini' @click='openScriptDialog(scope.row)'>编辑脚本</el-button>
                    </template>
                </el-table-column>
            </el-table>
            <div class='pagination' style='margin-top: 12px'>
                <el-pagination
                    :current-page='versionQuery.pageNum'
                    :page-sizes='[10, 15, 30, 50]'
                    :page-size='versionQuery.pageSize'
                    :total='versionQuery.total'
                    background
                    layout='sizes, prev, pager, next'
                    @size-change='handleVersionSizeChange'
                    @current-change='handleVersionPagination'
                />
            </div>
        </el-dialog>
        
        <el-dialog title='修改函数信息' :visible.sync='editMetaVisible' append-to-body width='520px'>
            <el-form :model='editMetaForm' label-width='100px' size='small'>
                <el-form-item label='变更日志'>
                    <el-input v-model.trim='editMetaForm.changeLog' type='textarea'
                              :autosize='{ minRows: 2, maxRows: 6 }' />
                </el-form-item>
                <el-form-item label='描述'>
                    <el-input v-model.trim='editMetaForm.description' type='textarea'
                              :autosize='{ minRows: 3, maxRows: 8 }' />
                </el-form-item>
            </el-form>
            <div slot='footer' class='dialog-footer'>
                <el-button @click='editMetaVisible = false'>取消</el-button>
                <el-button type='primary' :loading='editMetaSubmitting' @click='submitEditMeta'>保存</el-button>
            </div>
        </el-dialog>
        
        <el-dialog title='导入脚本' :visible.sync='importScriptVisible' append-to-body width='560px'
                   @closed='resetImportScriptForm'>
            <el-form :model='importScriptForm' label-width='100px' size='small'>
                <el-form-item label='目标版本'>
                    <el-input :value='importVersionLabel' disabled />
                </el-form-item>
                <el-form-item label='Zip 文件' required>
                    <input type='file' accept='.zip,application/zip' @change='onImportZipChange' />
                    <div v-if='importScriptForm.zipFileName' style='margin-top: 6px; color: #666'>
                        {{ importScriptForm.zipFileName }}
                    </div>
                </el-form-item>
                <el-form-item label='变更日志' required>
                    <el-input
                        v-model.trim='importScriptForm.changeLog'
                        type='textarea'
                        :autosize='{ minRows: 3, maxRows: 8 }'
                        placeholder='请输入本次导入的变更说明'
                    />
                </el-form-item>
            </el-form>
            <div slot='footer' class='dialog-footer'>
                <el-button @click='importScriptVisible = false'>取消</el-button>
                <el-button type='primary' :loading='importScriptSubmitting' @click='submitImportScript'>确认导入
                </el-button>
            </div>
        </el-dialog>
        
        <el-dialog title='执行函数测试' :visible.sync='executeDialogVisible' append-to-body width='740px'
                   @closed='resetExecuteForm'>
            <el-form :model='executeForm' label-width='110px' size='small'>
                <el-form-item label='版本'>
                    <el-input :value='executeVersionLabel' disabled />
                </el-form-item>
                <el-form-item label='环境' required>
                    <el-radio-group v-model='executeForm.testMode'>
                        <el-radio :label='true' :disabled='!executeVersionRow || !executeVersionRow.testActive'>
                            测试环境
                        </el-radio>
                        <el-radio :label='false' :disabled='!executeVersionRow || !executeVersionRow.proActive'>
                            生产环境
                        </el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label='输入参数'>
                    <el-input
                        v-model.trim='executeForm.inputText'
                        type='textarea'
                        :autosize='{ minRows: 8, maxRows: 14 }'
                        placeholder='请输入 JSON，例如 {"uid":"1001"}'
                    />
                </el-form-item>
            </el-form>
            <div v-if='executeResult' class='execute-result'>
                <div class='result-head'>
                    <span>执行结果</span>
                    <el-button type='text' v-if='executeResult._requestId' @click='openLoki(executeResult._requestId)'
                               size='mini'>
                        日志
                    </el-button>
                    <el-tag :type="executeResult.code === 200 ? 'success' : 'danger'" size='mini'>
                        code={{ executeResult.code }}
                    </el-tag>
                </div>
                <vue-json-pretty :data='executeResult' />
            </div>
            <div slot='footer' class='dialog-footer'>
                <el-button @click='executeDialogVisible = false'>取消</el-button>
                <el-button type='primary' :loading='executeSubmitting' @click='submitExecute'>执行</el-button>
            </div>
        </el-dialog>
        
        <cloud-function-script-dialog
            :visible.sync='scriptDialogVisible'
            :version-row='scriptVersionRow'
            :function-type='scriptFunctionType'
            :function-name='scriptFunctionName'
            @success='onScriptSubmitSuccess'
        />
    </section>
</template>

<script>
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';
import { message } from '@/utils/message';
import { queryProjectInfoList } from '@api/ProjectInfo';
import { cloudFunctionApi } from '@/components/api/Other/cloudFunctionApi';
import { nacosApi } from '@/components/api/nacos/nacosApi';
import CloudFunctionScriptDialog from './CloudFunctionScriptDialog.vue';

const FUNCTION_TYPE = {
    PROJECT: 'PROJECT',
    PROJECT_COMMON: 'PROJECT_COMMON'
};

export default {
    name: 'CloudFunctionManage',
    components: {
        CloudFunctionScriptDialog,
        VueJsonPretty
    },
    data() {
        return {
            FUNCTION_TYPE,
            loading: false,
            showSearch: true,
            tableList: [],
            projectInfoList: [],
            projectCnNameMap: new Map(),
            projectNameMap: new Map(),
            selectedProjectAppId: undefined,
            queryParams: this.newQueryParams(),
            typeOptions: [
                { label: '业务函数', value: FUNCTION_TYPE.PROJECT },
                { label: '项目通用函数', value: FUNCTION_TYPE.PROJECT_COMMON }
            ],
            addDialogVisible: false,
            addSubmitting: false,
            addForm: this.newAddForm(),
            versionDialogVisible: false,
            versionGroupRow: null,
            versionLoading: false,
            versionList: [],
            versionQuery: this.newVersionQuery(),
            editMetaVisible: false,
            editMetaSubmitting: false,
            editMetaForm: {
                id: null,
                changeLog: '',
                description: ''
            },
            scriptDialogVisible: false,
            scriptVersionRow: null,
            scriptFunctionType: FUNCTION_TYPE.PROJECT,
            scriptFunctionName: '',
            importScriptVisible: false,
            importScriptSubmitting: false,
            importVersionRow: null,
            importScriptForm: this.newImportScriptForm(),
            executeDialogVisible: false,
            executeSubmitting: false,
            executeForm: this.newExecuteForm(),
            executeVersionRow: null,
            executeResult: null
        };
    },
    computed: {
        versionDialogTitle() {
            if (!this.versionGroupRow) {
                return '云函数版本';
            }
            const cnName = this.projectCnNameMap.get(this.versionGroupRow.projectAppId) || this.versionGroupRow.projectAppId;
            return `版本列表 - ${cnName} / ${this.versionGroupRow.name}`;
        },
        executeVersionLabel() {
            if (!this.executeVersionRow) {
                return '';
            }
            return `v${this.executeVersionRow.version} (#${this.executeVersionRow.id})`;
        },
        importVersionLabel() {
            if (!this.importVersionRow) {
                return '';
            }
            return `v${this.importVersionRow.version} (#${this.importVersionRow.id})`;
        }
    },
    created() {
        this.loadProjectList();
    },
    methods: {
        newQueryParams() {
            return {
                total: 0,
                pageNum: 1,
                pageSize: 15,
                projectAppId: undefined,
                type: undefined,
                name: undefined,
                sortSqlList: ['createTime desc']
            };
        },
        newVersionQuery() {
            return {
                total: 0,
                pageNum: 1,
                pageSize: 15,
                sortSqlList: ['version desc', 'id desc']
            };
        },
        newAddForm() {
            return {
                projectAppId: undefined,
                type: FUNCTION_TYPE.PROJECT,
                name: '',
                description: ''
            };
        },
        newExecuteForm() {
            return {
                testMode: true,
                inputText: '{}'
            };
        },
        newImportScriptForm() {
            return {
                changeLog: '',
                zipFile: null,
                zipFileName: ''
            };
        },
        typeLabel(type) {
            const target = this.typeOptions.find((item) => item.value === type);
            return target ? target.label : type;
        },
        loadProjectList() {
            queryProjectInfoList({ pageSize: 100000 }).then((res) => {
                if (res.code !== 200 || !res.data || !res.data.result) {
                    return;
                }
                this.projectInfoList = res.data.result || [];
                this.projectCnNameMap = new Map();
                this.projectNameMap = new Map();
                this.projectInfoList.forEach((item) => {
                    this.projectCnNameMap.set(item.appId, item.cnName);
                    this.projectNameMap.set(item.appId, item.name);
                });
            });
        },
        handleProjectSelect(val) {
            if (val) {
                this.getList();
            }
        },
        normalizedQueryPayload() {
            const payload = { ...this.queryParams };
            payload.projectAppId = this.selectedProjectAppId;
            payload.type = payload.type || undefined;
            payload.name = payload.name ? String(payload.name).trim() : undefined;
            if (!payload.name) {
                payload.name = undefined;
            }
            return payload;
        },
        getList() {
            if (!this.selectedProjectAppId) return;
            this.loading = true;
            cloudFunctionApi.queryCloudFunctionList(this.normalizedQueryPayload()).then((res) => {
                if (res.code === 200 && res.data) {
                    this.tableList = res.data.result || [];
                    this.queryParams.total = res.data.total != null ? res.data.total : 0;
                    if (res.data.pageNum != null) {
                        this.queryParams.pageNum = res.data.pageNum;
                    }
                }
                this.loading = false;
            });
        },
        handleQuery() {
            this.queryParams.pageNum = 1;
            this.getList();
        },
        resetQuery() {
            this.queryParams = this.newQueryParams();
            this.handleQuery();
        },
        handleSizeChange(size) {
            this.queryParams.pageSize = size;
            this.queryParams.pageNum = 1;
            this.getList();
        },
        handlePagination(pageNum) {
            this.queryParams.pageNum = pageNum;
            this.getList();
        },
        handleSortChange({ prop, order }) {
            let sortType = null;
            if (order === 'ascending') {
                sortType = 'ASC';
            } else if (order === 'descending') {
                sortType = 'DESC';
            }
            const next = (this.queryParams.sortSqlList || []).filter((item) => !item.startsWith(`${prop} `));
            if (sortType) {
                next.push(`${prop} ${sortType}`);
            }
            if (!next.length) {
                next.push('createTime desc');
            }
            this.queryParams.sortSqlList = next;
            this.getList();
        },
        openAddDialog() {
            this.resetAddForm();
            this.addDialogVisible = true;
        },
        resetAddForm() {
            this.addForm = this.newAddForm();
        },
        onAddTypeChange(type) {
            if (type === FUNCTION_TYPE.PROJECT_COMMON) {
                this.addForm.name = '';
            }
        },
        submitAdd() {
            if (!this.addForm.type) {
                message.error('请选择函数类型');
                return;
            }
            const payload = {
                projectAppId: this.selectedProjectAppId,
                type: this.addForm.type,
                description: this.addForm.description || undefined
            };
            if (this.addForm.type === FUNCTION_TYPE.PROJECT) {
                const name = String(this.addForm.name || '').trim();
                if (!name) {
                    message.error('请输入函数名');
                    return;
                }
                if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
                    message.error('函数名需字母开头，仅字母数字下划线');
                    return;
                }
                payload.name = name;
            }
            this.addSubmitting = true;
            cloudFunctionApi.insertCloudFunction(payload).then((res) => {
                if (res.code === 200) {
                    message.success('创建成功');
                    this.addDialogVisible = false;
                    this.getList();
                }
                this.addSubmitting = false;
            });
        },
        openVersionDialog(row) {
            this.versionGroupRow = { ...row };
            this.versionQuery = this.newVersionQuery();
            this.versionDialogVisible = true;
            this.loadVersionList();
        },
        closeVersionDialog() {
            this.versionGroupRow = null;
            this.versionList = [];
            this.versionQuery = this.newVersionQuery();
        },
        versionQueryPayload() {
            if (!this.versionGroupRow) {
                return null;
            }
            return {
                projectAppId: this.versionGroupRow.projectAppId,
                name: this.versionGroupRow.name,
                pageNum: this.versionQuery.pageNum,
                pageSize: this.versionQuery.pageSize,
                sortSqlList: this.versionQuery.sortSqlList
            };
        },
        loadVersionList() {
            const payload = this.versionQueryPayload();
            if (!payload) {
                return;
            }
            this.versionLoading = true;
            cloudFunctionApi.queryCloudFunctionVersionList(payload).then((res) => {
                if (res.code === 200 && res.data) {
                    this.versionList = res.data.result || [];
                    this.versionQuery.total = res.data.total != null ? res.data.total : 0;
                    if (res.data.pageNum != null) {
                        this.versionQuery.pageNum = res.data.pageNum;
                    }
                } else {
                    this.versionList = [];
                }
                this.versionLoading = false;
            });
        },
        handleVersionSizeChange(size) {
            this.versionQuery.pageSize = size;
            this.versionQuery.pageNum = 1;
            this.loadVersionList();
        },
        handleVersionPagination(pageNum) {
            this.versionQuery.pageNum = pageNum;
            this.loadVersionList();
        },
        openEditMeta(row) {
            this.editMetaForm = {
                id: row.id,
                changeLog: row.changeLog || '',
                description: row.description || ''
            };
            this.editMetaVisible = true;
        },
        submitEditMeta() {
            this.editMetaSubmitting = true;
            cloudFunctionApi.updateCloudFunction({
                id: this.editMetaForm.id,
                changeLog: this.editMetaForm.changeLog || undefined,
                description: this.editMetaForm.description || undefined
            }).then((res) => {
                if (res.code === 200) {
                    message.success('保存成功');
                    this.editMetaVisible = false;
                    this.loadVersionList();
                    this.getList();
                }
                this.editMetaSubmitting = false;
            });
        },
        async refreshFunctionOnAllInstances(row, isTestMode) {
            const projectName = this.projectNameMap.get(row.projectAppId);
            if (!projectName) {
                message.warning('刷新失败：项目缺少 projectName');
                return;
            }
            // 确定函数类型和名称。如果是版本列表中的操作，row 可能缺失 type，此时从 versionGroupRow 获取
            const targetType = row.type || (this.versionGroupRow ? this.versionGroupRow.type : null);
            const targetName = row.name || (this.versionGroupRow ? this.versionGroupRow.name : null);
            
            try {
                const nacosRes = await nacosApi.getInstanceList('cloudFunctionRuntime');
                if (nacosRes.code !== 200 || !nacosRes.data || !Array.isArray(nacosRes.data)) {
                    message.error('获取运行时实例列表失败');
                    return;
                }
                const instances = nacosRes.data;
                const promises = instances.map((ip) => {
                    if (targetType === FUNCTION_TYPE.PROJECT_COMMON) {
                        // 通用函数刷新：只调用 updateProjectCommonFunction
                        return cloudFunctionApi.system.updateProjectCommonFunction(projectName, isTestMode, ip);
                    } else {
                        // 业务函数刷新：只调用 updateFunctionVersion
                        return cloudFunctionApi.system.updateFunctionVersion(targetName, projectName, isTestMode, ip);
                    }
                });
                await Promise.allSettled(promises);
            } catch (e) {
                message.error('刷新实例失败');
            }
        },
        async handleRefresh(row) {
            try {
                await this.$modal.confirm(`确认刷新函数「${row.name}」生产和测试环境的缓存么？`);
            } catch (e) {
                return;
            }
            const loading = this.$loading({ lock: true, text: '正在刷新所有实例...', spinner: 'el-icon-loading' });
            try {
                const tasks = [];
                if (row.testActive) {
                    tasks.push(this.refreshFunctionOnAllInstances(row, true));
                }
                if (row.proActive) {
                    tasks.push(this.refreshFunctionOnAllInstances(row, false));
                }
                if (!tasks.length) {
                    message.warning('该函数当前未在任何环境激活，无需刷新');
                    return;
                }
                await Promise.allSettled(tasks);
                message.success('刷新指令已同步到所有实例');
            } finally {
                loading.close();
            }
        },
        async deployVersion(row, testMode) {
            const envText = testMode ? '测试环境' : '生产环境';
            try {
                await this.$modal.confirm(`确认将版本 v${row.version} 部署到${envText}？`);
                const deployRes = await cloudFunctionApi.deployFunction({ id: row.id, testMode });
                if (deployRes.code !== 200) {
                    return;
                }
                await this.refreshFunctionOnAllInstances(row, testMode);
                this.$modal.msgSuccess('部署成功，已触发所有实例刷新');
                this.loadVersionList();
                this.getList();
            } catch (e) {
            }
        },
        async handleDelete(row) {
            try {
                await this.$modal.confirm(`将删除函数「${row.name}」及全部版本，是否继续？`);
                const res = await cloudFunctionApi.deleteCloudFunction({ id: row.id });
                if (res.code !== 200) {
                    return;
                }
                await Promise.allSettled([
                    this.refreshFunctionOnAllInstances(row, true),
                    this.refreshFunctionOnAllInstances(row, false)
                ]);
                this.$modal.msgSuccess('删除成功，已同步到所有实例刷新');
                this.getList();
            } catch (e) {
            }
        },
        async openImportScriptDialog() {
            if (!this.versionGroupRow) {
                return;
            }
            const latestRow = await this.queryLatestVersionRow();
            if (!latestRow) {
                message.error('未找到可导入的目标版本');
                return;
            }
            this.importVersionRow = { ...latestRow };
            this.importScriptForm = this.newImportScriptForm();
            this.importScriptVisible = true;
        },
        async queryLatestVersionRow() {
            if (!this.versionGroupRow) {
                return null;
            }
            try {
                const payload = {
                    projectAppId: this.versionGroupRow.projectAppId,
                    name: this.versionGroupRow.name,
                    pageNum: 1,
                    pageSize: 1,
                    sortSqlList: ['version desc', 'id desc']
                };
                const res = await cloudFunctionApi.queryCloudFunctionVersionList(payload);
                if (res.code === 200 && res.data && Array.isArray(res.data.result) && res.data.result.length) {
                    return res.data.result[0];
                }
            } catch (e) {
            }
            return null;
        },
        resetImportScriptForm() {
            this.importScriptForm = this.newImportScriptForm();
            this.importVersionRow = null;
            this.importScriptSubmitting = false;
        },
        onImportZipChange(event) {
            const files = event && event.target ? event.target.files : null;
            const file = files && files[0] ? files[0] : null;
            this.importScriptForm.zipFile = file;
            this.importScriptForm.zipFileName = file ? file.name : '';
        },
        submitImportScript() {
            if (!this.importVersionRow) {
                return;
            }
            if (!this.importScriptForm.zipFile) {
                message.error('请选择 zip 文件');
                return;
            }
            if (!this.importScriptForm.changeLog) {
                message.error('请填写变更日志');
                return;
            }
            this.importScriptSubmitting = true;
            cloudFunctionApi.updateFunctionScript({
                id: this.importVersionRow.id,
                zipFile: this.importScriptForm.zipFile,
                changeLog: this.importScriptForm.changeLog
            }).then((res) => {
                if (res.code === 200) {
                    message.success('导入成功，已创建新版本');
                    this.importScriptVisible = false;
                    this.loadVersionList();
                    this.getList();
                }
                this.importScriptSubmitting = false;
            }).catch(() => {
                this.importScriptSubmitting = false;
            });
        },
        openScriptDialog(row) {
            this.scriptVersionRow = { ...row };
            this.scriptFunctionType = (this.versionGroupRow && this.versionGroupRow.type) || FUNCTION_TYPE.PROJECT;
            this.scriptFunctionName = (this.versionGroupRow && this.versionGroupRow.name) || row.name || '';
            this.scriptDialogVisible = true;
        },
        onScriptSubmitSuccess() {
            this.loadVersionList();
            this.getList();
        },
        openExecuteDialog(row) {
            if (!row.testActive && !row.proActive) {
                message.error('请先部署到测试或生产环境后再执行');
                return;
            }
            this.executeVersionRow = { ...row };
            this.executeForm = this.newExecuteForm();
            if (row.testActive) {
                this.executeForm.testMode = true;
            } else if (row.proActive) {
                this.executeForm.testMode = false;
            }
            this.executeResult = null;
            this.executeDialogVisible = true;
        },
        resetExecuteForm() {
            this.executeVersionRow = null;
            this.executeResult = null;
            this.executeSubmitting = false;
        },
        parseExecuteInput() {
            const text = String(this.executeForm.inputText || '').trim();
            if (!text) {
                return {};
            }
            try {
                return JSON.parse(text);
            } catch (e) {
                message.error('输入参数必须是合法 JSON');
                return null;
            }
        },
        submitExecute() {
            if (!this.executeVersionRow) {
                return;
            }
            if (this.executeForm.testMode && !this.executeVersionRow.testActive) {
                message.error('该版本当前不是测试环境激活版本');
                return;
            }
            if (!this.executeForm.testMode && !this.executeVersionRow.proActive) {
                message.error('该版本当前不是生产环境激活版本');
                return;
            }
            const projectName = this.projectNameMap.get(this.executeVersionRow.projectAppId);
            if (!projectName) {
                message.error('未找到项目 projectName');
                return;
            }
            const input = this.parseExecuteInput();
            if (input === null) {
                return;
            }
            this.executeSubmitting = true;
            cloudFunctionApi.system.executeFunction(
                this.executeVersionRow.name,
                projectName,
                this.executeForm.testMode,
                input
            ).then((res) => {
                if (res && res._headers) {
                    res._requestId = res._headers['x-request-id'];
                }
                this.executeResult = res || { code: -1, message: 'empty response', data: null };
                if (res && res.code === 200) {
                    message.success('执行成功');
                }
                this.executeSubmitting = false;
            }).catch(() => {
                this.executeSubmitting = false;
            });
        },
        openLoki(requestId) {
            message.success('执行日志上传需要一分钟左右, 可点击Loki面板中的刷新按钮进行查看');
            const url = `http://8.130.32.232:6500/a/grafana-lokiexplore-app/explore/service/cloudFunctionRuntime/logs?var-filters=
service_name|=|cloudFunctionRuntime&var-lineFilters=caseInsensitive,0|__gfp__=|${requestId}`;
            setTimeout(() => {
                window.open(url, '_blank');
            }, 3000);
        }
    }
};
</script>

<style scoped>
.execute-result {
    margin-top: 12px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 12px;
    background: #fafafa;
}

.result-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    font-weight: 600;
}

.version-dialog-title-row {
    width: calc(100% - 20px);
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
}
</style>
